import React, { useEffect, useState } from 'react';
import { Tooltip } from '@arco-design/web-react';
import { IconCommon, IconExport } from '@arco-design/web-react/icon';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMemoizedFn } from 'ahooks';
import { apiTrackingCheckAccess } from '@/api/tracking';
import Device from '@/utils/device';
import { getOrg } from '@/utils/hooks/useOrg';
import {
  isCurrentWindowFramed,
  resolveVisualTrackingPreviewMode,
} from '@/utils/business/visualTracking/previewMode';
import { notifyVisualTrackingConfigChanged } from '@/utils/business/visualTracking/configChange';
import styles from './index.module.less';

const TRACKING_RETURN_PATH_KEY = 'ehr-web:tracking-return-path';

export default function TrackingEntry() {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgId = '' } = getOrg();
  const [allowed, setAllowed] = useState(false);
  const isPreviewMode = resolveVisualTrackingPreviewMode({
    search: location.search,
    isFramed: isCurrentWindowFramed(),
    storage: typeof window === 'undefined' ? null : window.sessionStorage,
  });
  const isOnTrackingPage = /\/tracking\/?$/.test(location.pathname);

  useEffect(() => {
    if (Device.isMobile() || isPreviewMode) {
      setAllowed(false);
      return;
    }

    let canceled = false;
    apiTrackingCheckAccess()
      .then((res) => {
        if (!canceled) {
          setAllowed(Boolean(res.allowed));
        }
      })
      .catch(() => {
        if (!canceled) {
          setAllowed(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, [isPreviewMode]);

  const handleOpen = useMemoizedFn(() => {
    if (!orgId) {
      return;
    }
    const returnPath = `${location.pathname}${location.search}`;
    if (returnPath && !/\/tracking\/?$/.test(location.pathname)) {
      try {
        window.sessionStorage.setItem(TRACKING_RETURN_PATH_KEY, returnPath);
      } catch (error) {
        // ignore
      }
    }
    navigate(`/org/${orgId}/tracking`);
  });

  const handleExit = useMemoizedFn(() => {
    if (!orgId) {
      return;
    }
    notifyVisualTrackingConfigChanged();
    let returnPath = `/org/${orgId}/`;
    try {
      const saved = window.sessionStorage.getItem(TRACKING_RETURN_PATH_KEY);
      window.sessionStorage.removeItem(TRACKING_RETURN_PATH_KEY);
      if (
        saved &&
        saved.indexOf(`/org/${orgId}/`) === 0 &&
        !/\/tracking\/?(\?|$)/.test(saved)
      ) {
        returnPath = saved;
      }
    } catch (error) {
      // ignore
    }
    navigate(returnPath);
  });

  if (Device.isMobile() || isPreviewMode) {
    return null;
  }

  if (isOnTrackingPage) {
    return (
      <div className={styles.trackingEntry}>
        <Tooltip content="退出埋点后台">
          <IconExport onClick={handleExit} />
        </Tooltip>
      </div>
    );
  }

  if (!allowed) {
    return null;
  }

  return (
    <div className={styles.trackingEntry}>
      <Tooltip content="埋点后台">
        <IconCommon onClick={handleOpen} />
      </Tooltip>
    </div>
  );
}
